import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaEnvelope as FaEnvelopeRaw,
  FaEye as FaEyeRaw,
  FaKey as FaKeyRaw,
  FaPaperPlane as FaPaperPlaneRaw,
  FaUsers as FaUsersRaw,
} from 'react-icons/fa';
import {
  useCreateMarketingCampaign,
  useCreateMarketingFlow,
  useCreateMarketingFlowStep,
  useCreateMarketingKeyword,
  useCreateMarketingTagSet,
  useCreateMarketingTemplate,
  useDeleteMarketingCampaign,
  useDeleteMarketingFlow,
  useDeleteMarketingFlowStep,
  useDeleteMarketingKeyword,
  useDeleteMarketingTagSet,
  useDeleteMarketingTemplate,
  useMarketingCampaigns,
  useMarketingFlows,
  useMarketingFlowSteps,
  useMarketingKeywords,
  useMarketingTagSets,
  useMarketingTemplates,
  useUpdateMarketingCampaign,
  useUpdateMarketingFlow,
  useUpdateMarketingKeyword,
  useUpdateMarketingTagSet,
  useUpdateMarketingTemplate,
} from '../hooks/useMarketing';
import MarketingHeader from '../components/MarketingHeader';
import SyncClearstreamButton from '../components/SyncClearstreamButton';
import {
  MarketingPageShell,
  Table as SharedTable,
  TableWrap as SharedTableWrap,
  PrimaryButton as SharedPrimaryButton,
  PrimaryButtonLink as SharedPrimaryButtonLink,
  SecondaryButton as SharedSecondaryButton,
  DangerButton as SharedDangerButton,
} from '../styles/marketingSharedStyles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaEnvelope: any = FaEnvelopeRaw;
const FaEye: any = FaEyeRaw;
const FaKey: any = FaKeyRaw;
const FaPaperPlane: any = FaPaperPlaneRaw;
const FaUsers: any = FaUsersRaw;

const Page = styled(MarketingPageShell)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 0;
  padding-bottom: 1rem;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const KpiCard = styled.div`
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.01) 100%), var(--surfaceVariant);
  border-radius: 16px;
  padding: 1rem 1.25rem;
  border: 1px solid var(--outline);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  min-width: 0;
`;
const KpiLabel = styled.div`
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;
const KpiValue = styled.div`
  font-size: clamp(1.25rem, 2vw, 1.75rem);
  font-weight: 700;
  color: var(--onSurface);
`;
const KpiChange = styled.span<{ $positive?: boolean }>`
  font-size: 0.8rem;
  color: ${({ $positive }) => ($positive === true ? 'var(--success)' : $positive === false ? 'var(--error)' : 'var(--onSurfaceVariant)')};
  margin-top: 0.25rem;
  display: inline-block;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 1.5rem;
  min-height: 0;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.section`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.18);
  min-width: 0;
  @media (max-width: 640px) {
    padding: 1rem;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 0.75rem;
  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;
const CardTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--onSurface);
`;
const CardSub = styled.p`
  margin: 0.25rem 0 0 0;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;

const EngagementChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  height: 160px;
  padding: 0.5rem 0;
  overflow-x: auto;
  min-width: 0;
`;
const Bar = styled.div<{ $height: number; $highlight?: boolean }>`
  flex: 1;
  min-width: 24px;
  background: ${({ $highlight }) => ($highlight ? 'var(--primary)' : 'var(--primaryDark)')};
  border-radius: 6px 6px 0 0;
  height: ${({ $height }) => Math.max(12, $height)}%;
  opacity: ${({ $highlight }) => ($highlight ? 1 : 0.75)};
`;
const ChartLabels = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: var(--onSurfaceVariant);
`;

const TimeSelect = styled.select`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.45rem 0.7rem;
  font-size: 0.8rem;
  color: var(--onSurface);
  background: var(--surface);
`;

const StatusBadge = styled.span<{ $variant: 'delivered' | 'upcoming' | 'draft' }>`
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${({ $variant }) =>
    $variant === 'delivered' ? 'var(--successContainer)' : $variant === 'upcoming' ? 'var(--surfaceVariant)' : 'var(--elevation2)'};
  color: ${({ $variant }) =>
    $variant === 'delivered' ? 'var(--success)' : $variant === 'upcoming' ? 'var(--primary)' : 'var(--onSurfaceVariant)'};
`;
const ViewAllLink = styled.button`
  background: none;
  border: none;
  color: var(--primary);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const ReachItem = styled.div`
  margin-bottom: 1rem;
  &:last-child { margin-bottom: 0; }
`;
const ReachLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  margin-bottom: 0.35rem;
  color: var(--onSurfaceVariant);
`;
const ReachBar = styled.div<{ $pct: number }>`
  height: 8px;
  background: var(--outlineVariant);
  border-radius: 4px;
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    width: ${({ $pct }) => $pct}%;
    height: 100%;
    background: var(--primary);
    border-radius: 4px;
  }
`;

const ProTip = styled.div`
  margin-top: 1.25rem;
  padding: 1rem;
  background: linear-gradient(130deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.015) 100%), var(--surfaceVariant);
  border-radius: 14px;
  border: 1px solid var(--outline);
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;
const ProTipIcon = styled.span`
  color: var(--primary);
  font-size: 1.1rem;
  flex-shrink: 0;
`;
const ProTipText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
  line-height: 1.45;
`;

const HeaderCard = styled.section`
  border: 1px solid var(--outline);
  border-radius: 16px;
  background: var(--surface);
  padding: 1rem 1.25rem;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.14);
  @media (max-width: 640px) {
    padding: 0.95rem;
  }
`;

const Title = styled.h1`
  margin: 0 0 0.25rem 0;
  font-size: clamp(1.35rem, 2.4vw, 1.85rem);
  color: var(--onSurface);
`;

const Subtle = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
`;

const Grid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SectionCard = styled.section`
  border: 1px solid var(--outline);
  border-radius: 16px;
  background: var(--surface);
  padding: 1rem;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.14);
  min-width: 0;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.25rem 0;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const SectionHint = styled.p`
  margin: 0 0 0.75rem 0;
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
`;

const FormRow = styled.form`
  display: grid;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const Input = styled.input`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
  background: var(--surface);
  color: var(--onSurface);
`;

const TextArea = styled.textarea`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
  min-height: 80px;
  resize: vertical;
  background: var(--surface);
  color: var(--onSurface);
`;

const Select = styled.select`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
  background: var(--surface);
  color: var(--onSurface);
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
`;


const Empty = styled.div`
  color: var(--onSurfaceVariant);
  font-size: 0.88rem;
`;

const Loading = styled.div`
  color: var(--onSurfaceVariant);
  font-size: 0.88rem;
`;

const ErrorText = styled.div`
  color: var(--error);
  font-size: 0.88rem;
`;

const ComingSoon = styled.div`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: var(--onWarningContainer);
  background: var(--warningContainer);
  border: 1px solid var(--warning);
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
`;

function formatRelativeDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const MarketingPage: React.FC = () => {
  const templates = useMarketingTemplates();
  const flows = useMarketingFlows();
  const campaigns = useMarketingCampaigns();
  const keywords = useMarketingKeywords();
  const tagSets = useMarketingTagSets();
  const flowSteps = useMarketingFlowSteps(flows.data?.[0]?.id);

  const createTemplate = useCreateMarketingTemplate();
  const updateTemplate = useUpdateMarketingTemplate();
  const deleteTemplate = useDeleteMarketingTemplate();

  const createFlow = useCreateMarketingFlow();
  const updateFlow = useUpdateMarketingFlow();
  const deleteFlow = useDeleteMarketingFlow();
  const createFlowStep = useCreateMarketingFlowStep();
  const deleteFlowStep = useDeleteMarketingFlowStep();

  const createCampaign = useCreateMarketingCampaign();
  const updateCampaign = useUpdateMarketingCampaign();
  const deleteCampaign = useDeleteMarketingCampaign();

  const createKeyword = useCreateMarketingKeyword();
  const updateKeyword = useUpdateMarketingKeyword();
  const deleteKeyword = useDeleteMarketingKeyword();

  const createTagSet = useCreateMarketingTagSet();
  const updateTagSet = useUpdateMarketingTagSet();
  const deleteTagSet = useDeleteMarketingTagSet();

  const [templateName, setTemplateName] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [templateChannel, setTemplateChannel] = useState<'sms' | 'email'>('sms');

  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');

  const [campaignName, setCampaignName] = useState('');
  const [campaignChannel, setCampaignChannel] = useState<'sms' | 'email'>('sms');
  const [campaignTemplateId, setCampaignTemplateId] = useState('');
  const [campaignFlowId, setCampaignFlowId] = useState('');

  const [keyword, setKeyword] = useState('');
  const [keywordDescription, setKeywordDescription] = useState('');
  const [keywordTemplateId, setKeywordTemplateId] = useState('');
  const [keywordFlowId, setKeywordFlowId] = useState('');

  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');
  const [tagColor, setTagColor] = useState('#d7c4ad');

  const flowIdForSteps = flows.data?.[0]?.id;
  const canCreateStep = !!flowIdForSteps;

  const templateOptions = useMemo(
    () => (templates.data ?? []).map((template) => ({ value: template.id, label: template.name })),
    [templates.data]
  );
  const flowOptions = useMemo(
    () => (flows.data ?? []).map((flow) => ({ value: flow.id, label: flow.name })),
    [flows.data]
  );

  const handleError = (error: unknown, fallback: string) => {
    const message = error instanceof Error ? error.message : fallback;
    window.alert(message);
  };

  const [view, setView] = useState<'dashboard' | 'workspace'>('dashboard');

  const campaignsList = useMemo(() => campaigns.data ?? [], [campaigns.data]);
  const extractCampaignStats = (campaignMetadata: Record<string, unknown>) => {
    const rawStats =
      typeof campaignMetadata.twilio_stats === 'object' && campaignMetadata.twilio_stats
        ? (campaignMetadata.twilio_stats as Record<string, unknown>)
        : {};
    const sent = Number(rawStats.sent_total ?? 0);
    const failed = Number(rawStats.failed_total ?? 0);
    const total = sent + failed;
    const deliveryRate = total > 0 ? Math.round((sent / total) * 100) : 0;
    return { sent, failed, deliveryRate };
  };

  const campaignStatsSummary = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const launchedCampaigns = campaignsList.filter((campaign) => {
      const metadata = (campaign.metadata ?? {}) as Record<string, unknown>;
      const { sent, failed } = extractCampaignStats(metadata);
      return sent + failed > 0;
    });
    const messagesThisMonth = campaignsList.reduce((sum, campaign) => {
      const metadata = (campaign.metadata ?? {}) as Record<string, unknown>;
      const launchMeta =
        typeof metadata.twilio_last_launch === 'object' && metadata.twilio_last_launch
          ? (metadata.twilio_last_launch as Record<string, unknown>)
          : {};
      const launchedAt = typeof launchMeta.launched_at === 'string' ? new Date(launchMeta.launched_at) : null;
      if (!launchedAt || Number.isNaN(launchedAt.getTime()) || launchedAt < monthStart) {
        return sum;
      }
      return sum + Number(launchMeta.sent ?? 0);
    }, 0);
    const sentTotal = launchedCampaigns.reduce((sum, campaign) => {
      const metadata = (campaign.metadata ?? {}) as Record<string, unknown>;
      const { sent } = extractCampaignStats(metadata);
      return sum + sent;
    }, 0);
    const failedTotal = launchedCampaigns.reduce((sum, campaign) => {
      const metadata = (campaign.metadata ?? {}) as Record<string, unknown>;
      const { failed } = extractCampaignStats(metadata);
      return sum + failed;
    }, 0);
    const deliveryRate = sentTotal + failedTotal > 0 ? Math.round((sentTotal / (sentTotal + failedTotal)) * 100) : 0;
    return {
      launchedCampaignCount: launchedCampaigns.length,
      messagesThisMonth,
      sentTotal,
      failedTotal,
      deliveryRate,
    };
  }, [campaignsList]);
  const recentCampaigns = useMemo(
    () => [...campaignsList].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()).slice(0, 5),
    [campaignsList]
  );

  const engagementBars = useMemo(() => {
    const now = new Date();
    const lastSevenDays = Array.from({ length: 7 }, (_, idx) => {
      const day = new Date(now);
      day.setDate(now.getDate() - (6 - idx));
      day.setHours(0, 0, 0, 0);
      return day;
    });
    const values = lastSevenDays.map((day) => {
      const nextDay = new Date(day);
      nextDay.setDate(day.getDate() + 1);
      return campaignsList.reduce((sum, campaign) => {
        const metadata = (campaign.metadata ?? {}) as Record<string, unknown>;
        const launchMeta =
          typeof metadata.twilio_last_launch === 'object' && metadata.twilio_last_launch
            ? (metadata.twilio_last_launch as Record<string, unknown>)
            : {};
        const launchedAt = typeof launchMeta.launched_at === 'string' ? new Date(launchMeta.launched_at) : null;
        if (!launchedAt || Number.isNaN(launchedAt.getTime()) || launchedAt < day || launchedAt >= nextDay) {
          return sum;
        }
        return sum + Number(launchMeta.sent ?? 0);
      }, 0);
    });
    return values.some((value) => value > 0) ? values : [0, 0, 0, 0, 0, 0, 0];
  }, [campaignsList]);
  const maxBar = Math.max(...engagementBars);
  const reachItems = useMemo(
    () => {
      const launched = campaignsList
        .map((campaign) => {
          const metadata = (campaign.metadata ?? {}) as Record<string, unknown>;
          const { sent } = extractCampaignStats(metadata);
          return { label: campaign.name, views: sent };
        })
        .filter((item) => item.views > 0)
        .sort((a, b) => b.views - a.views)
        .slice(0, 3);
      const max = Math.max(launched[0]?.views ?? 1, 1);
      return launched.map((item) => ({ ...item, max }));
    },
    [campaignsList]
  );

  if (view === 'dashboard') {
    return (
      <Page>
        <MarketingHeader
          activeTab="dashboard"
          rightSlot={
            <HeaderActions>
              <SyncClearstreamButton />
              <SharedPrimaryButtonLink to="/marketing/new-message">
                <FaPaperPlane size={14} /> New Message
              </SharedPrimaryButtonLink>
            </HeaderActions>
          }
        />
        <KpiGrid>
          <KpiCard>
            <KpiLabel><FaUsers size={12} /> Total Messages Sent</KpiLabel>
            <KpiValue>{campaignStatsSummary.sentTotal.toLocaleString()}</KpiValue>
            <KpiChange $positive={campaignStatsSummary.sentTotal > 0}>
              Sent to subscribers
            </KpiChange>
          </KpiCard>
          <KpiCard>
            <KpiLabel><FaPaperPlane size={12} /> Monthly messages</KpiLabel>
            <KpiValue>{campaignStatsSummary.messagesThisMonth.toLocaleString()}</KpiValue>
            <KpiChange $positive={campaignStatsSummary.messagesThisMonth > 0}>
              Current month sends
            </KpiChange>
          </KpiCard>
          <KpiCard>
            <KpiLabel><FaEye size={12} /> Microsite views</KpiLabel>
            <KpiValue>{campaignStatsSummary.deliveryRate}%</KpiValue>
            <KpiChange $positive={campaignStatsSummary.deliveryRate >= 90}>
              Delivery success rate
            </KpiChange>
          </KpiCard>
          <KpiCard>
            <KpiLabel><FaKey size={12} /> Keywords</KpiLabel>
            <KpiValue>{(keywords.data ?? []).filter((item) => item.is_active).length}</KpiValue>
            <KpiChange $positive={(keywords.data ?? []).some((item) => item.is_active)}>
              Active keywords
            </KpiChange>
          </KpiCard>
        </KpiGrid>

        <MainGrid>
          <MainColumn>
            <Card>
              <CardHeader style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                <div>
                  <CardTitle>Engagement Overview</CardTitle>
                  <CardSub>Campaign performance for the last 30 days</CardSub>
                </div>
                <TimeSelect>
                  <option>Last 30 Days</option>
                  <option>Last 7 Days</option>
                  <option>Last 90 Days</option>
                </TimeSelect>
              </CardHeader>
              <EngagementChart>
                {engagementBars.map((val, i) => (
                  <Bar key={i} $height={(val / maxBar) * 100} $highlight={i === 4} />
                ))}
              </EngagementChart>
              <ChartLabels>
                <span>Week 1</span>
                <span>Week 2</span>
                <span>Week 3</span>
                <span>Week 4</span>
                <span>Today</span>
                <span>Next</span>
                <span>Forecast</span>
              </ChartLabels>
            </Card>

            <Card>
              <CardHeader>
                <div>
                  <CardTitle>Recent Activity</CardTitle>
                  <CardSub>Latest messages</CardSub>
                </div>
                <ViewAllLink type="button" onClick={() => setView('workspace')}>
                  View all messages
                </ViewAllLink>
              </CardHeader>
              {campaigns.isLoading && <Loading>Loading messages...</Loading>}
              {!campaigns.isLoading && recentCampaigns.length === 0 && (
                <Empty>No messages yet. Create one in Workspace.</Empty>
              )}
              {!campaigns.isLoading && recentCampaigns.length > 0 && (
                <SharedTableWrap>
                  <SharedTable>
                    <thead>
                      <tr>
                        <th>Message name</th>
                        <th>Type</th>
                        <th>Recipients</th>
                        <th>Delivery rate</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentCampaigns.map((c) => (
                        <tr key={c.id}>
                          <td>{c.name}</td>
                          <td>
                            {c.channel === 'sms' ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                <FaPaperPlane size={12} /> SMS
                              </span>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                <FaEnvelope size={12} /> Email
                              </span>
                            )}
                          </td>
                          <td>-</td>
                          <td>
                            {(() => {
                              const metadata = (c.metadata ?? {}) as Record<string, unknown>;
                              const { deliveryRate } = extractCampaignStats(metadata);
                              const displayRate = deliveryRate > 0 ? `${deliveryRate}%` : '-';
                              return (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                  <span style={{ width: 48, height: 6, background: 'var(--outlineVariant)', borderRadius: 3, overflow: 'hidden' }}>
                                    <span style={{ display: 'block', width: deliveryRate > 0 ? `${deliveryRate}%` : '0%', height: '100%', background: 'var(--success)', borderRadius: 3 }} />
                                  </span>
                                  {displayRate}
                                </span>
                              );
                            })()}
                          </td>
                          <td>
                            {(() => {
                              const metadata = (c.metadata ?? {}) as Record<string, unknown>;
                              const { sent } = extractCampaignStats(metadata);
                              const hasLaunched = sent > 0;
                              const variant = hasLaunched
                                ? 'delivered'
                                : c.status === 'scheduled'
                                  ? 'upcoming'
                                  : 'draft';
                              const label = hasLaunched
                                ? 'Delivered'
                                : c.status === 'scheduled'
                                  ? 'Queued'
                                  : 'Draft';
                              return <StatusBadge $variant={variant}>{label}</StatusBadge>;
                            })()}
                          </td>
                          <td>{formatRelativeDate(c.updated_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </SharedTable>
                </SharedTableWrap>
              )}
            </Card>
          </MainColumn>

          <aside>
            <Card>
              <CardHeader>
                <CardTitle>Microsite Reach</CardTitle>
              </CardHeader>
              {reachItems.length === 0 && (
                <Empty>No launched SMS campaigns yet.</Empty>
              )}
              {reachItems.map((r) => (
                <ReachItem key={r.label}>
                  <ReachLabel>
                    <span>{r.label}</span>
                    <span>{r.views.toLocaleString()} recipients</span>
                  </ReachLabel>
                  <ReachBar $pct={(r.views / r.max) * 100} />
                </ReachItem>
              ))}
              <ProTip>
                <ProTipIcon>✓</ProTipIcon>
                <ProTipText>
                  Add a keyword to your microsites to increase organic traffic by up to 30%.
                </ProTipText>
              </ProTip>
            </Card>
          </aside>
        </MainGrid>
      </Page>
    );
  }

  return (
    <Page>
      <MarketingHeader
        activeTab="dashboard"
        rightSlot={
          <HeaderActions>
            <SyncClearstreamButton />
            <SharedPrimaryButtonLink to="/marketing/new-message">
              <FaPaperPlane size={14} /> New Message
            </SharedPrimaryButtonLink>
          </HeaderActions>
        }
      />
      <HeaderCard>
        <Title>Marketing</Title>
        <Subtle>
          Draft-first marketing workspace for templates, flows, campaigns, text-to-join keywords, and
          tags.
        </Subtle>
      </HeaderCard>

      <Grid>
        <SectionCard>
          <SectionTitle>Messages</SectionTitle>
          <SectionHint>Create reusable SMS or email templates.</SectionHint>
          <FormRow
            onSubmit={async (event) => {
              event.preventDefault();
              if (!templateName.trim() || !templateBody.trim()) return;
              try {
                await createTemplate.mutateAsync({
                  name: templateName.trim(),
                  body: templateBody.trim(),
                  channel: templateChannel,
                });
                setTemplateName('');
                setTemplateBody('');
              } catch (error) {
                handleError(error, 'Failed to create template.');
              }
            }}
          >
            <Input
              placeholder="Template name"
              value={templateName}
              onChange={(event) => setTemplateName(event.target.value)}
            />
            <Select
              value={templateChannel}
              onChange={(event) => setTemplateChannel(event.target.value as 'sms' | 'email')}
            >
              <option value="sms">SMS</option>
              <option value="email">Email</option>
            </Select>
            <TextArea
              placeholder="Template body"
              value={templateBody}
              onChange={(event) => setTemplateBody(event.target.value)}
            />
            <SharedPrimaryButton type="submit" disabled={createTemplate.isPending}>
              {createTemplate.isPending ? 'Saving...' : 'Add template'}
            </SharedPrimaryButton>
          </FormRow>

          {templates.isLoading && <Loading>Loading templates...</Loading>}
          {templates.error && <ErrorText>Unable to load templates.</ErrorText>}
          {!templates.isLoading && !templates.error && (templates.data?.length ?? 0) === 0 && (
            <Empty>No templates yet.</Empty>
          )}
          {(templates.data?.length ?? 0) > 0 && (
            <SharedTableWrap>
              <SharedTable>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.data?.map((template) => (
                    <tr key={template.id}>
                      <td>{template.name}</td>
                      <td>{template.channel}</td>
                      <td>{template.status}</td>
                      <td>
                        <RowActions>
                          <SharedSecondaryButton
                            type="button"
                            onClick={async () => {
                              try {
                                const nextStatus =
                                  template.status === 'draft' ? 'active' : template.status === 'active' ? 'archived' : 'draft';
                                await updateTemplate.mutateAsync({
                                  id: template.id,
                                  changes: { status: nextStatus },
                                });
                              } catch (error) {
                                handleError(error, 'Failed to update template status.');
                              }
                            }}
                          >
                            Cycle status
                          </SharedSecondaryButton>
                          <SharedDangerButton
                            type="button"
                            onClick={async () => {
                              if (!window.confirm('Delete this template?')) return;
                              try {
                                await deleteTemplate.mutateAsync(template.id);
                              } catch (error) {
                                handleError(error, 'Failed to delete template.');
                              }
                            }}
                          >
                            Delete
                          </SharedDangerButton>
                        </RowActions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </SharedTable>
            </SharedTableWrap>
          )}
        </SectionCard>

        <SectionCard>
          <SectionTitle>Flows</SectionTitle>
          <SectionHint>Build flow definitions and starter steps.</SectionHint>
          <FormRow
            onSubmit={async (event) => {
              event.preventDefault();
              if (!flowName.trim()) return;
              try {
                await createFlow.mutateAsync({
                  name: flowName.trim(),
                  description: flowDescription.trim() || null,
                });
                setFlowName('');
                setFlowDescription('');
              } catch (error) {
                handleError(error, 'Failed to create flow.');
              }
            }}
          >
            <Input
              placeholder="Flow name"
              value={flowName}
              onChange={(event) => setFlowName(event.target.value)}
            />
            <TextArea
              placeholder="Flow description"
              value={flowDescription}
              onChange={(event) => setFlowDescription(event.target.value)}
            />
            <SharedPrimaryButton type="submit" disabled={createFlow.isPending}>
              {createFlow.isPending ? 'Saving...' : 'Add flow'}
            </SharedPrimaryButton>
          </FormRow>

          {flows.isLoading && <Loading>Loading flows...</Loading>}
          {flows.error && <ErrorText>Unable to load flows.</ErrorText>}
          {!flows.isLoading && !flows.error && (flows.data?.length ?? 0) === 0 && <Empty>No flows yet.</Empty>}
          {(flows.data?.length ?? 0) > 0 && (
            <SharedTableWrap>
              <SharedTable>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {flows.data?.map((flow) => (
                    <tr key={flow.id}>
                      <td>{flow.name}</td>
                      <td>{flow.status}</td>
                      <td>
                        <RowActions>
                          <SharedSecondaryButton
                            type="button"
                            onClick={async () => {
                              try {
                                const nextStatus =
                                  flow.status === 'draft' ? 'active' : flow.status === 'active' ? 'paused' : 'draft';
                                await updateFlow.mutateAsync({ id: flow.id, changes: { status: nextStatus } });
                              } catch (error) {
                                handleError(error, 'Failed to update flow status.');
                              }
                            }}
                          >
                            Cycle status
                          </SharedSecondaryButton>
                          <SharedDangerButton
                            type="button"
                            onClick={async () => {
                              if (!window.confirm('Delete this flow?')) return;
                              try {
                                await deleteFlow.mutateAsync(flow.id);
                              } catch (error) {
                                handleError(error, 'Failed to delete flow.');
                              }
                            }}
                          >
                            Delete
                          </SharedDangerButton>
                        </RowActions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </SharedTable>
            </SharedTableWrap>
          )}

          <SectionHint style={{ marginTop: '0.75rem' }}>
            Flow steps are currently attached to the first flow in the list for scaffolding.
          </SectionHint>
          <RowActions>
            <SharedSecondaryButton
              type="button"
              disabled={!canCreateStep}
              onClick={async () => {
                if (!flowIdForSteps) return;
                const currentSteps = flowSteps.data ?? [];
                try {
                  await createFlowStep.mutateAsync({
                    flowId: flowIdForSteps,
                    stepOrder: currentSteps.length + 1,
                    stepType: 'wait',
                    config: { durationMinutes: 60 },
                  });
                } catch (error) {
                  handleError(error, 'Failed to create flow step.');
                }
              }}
            >
              Add step to first flow
            </SharedSecondaryButton>
          </RowActions>
          {(flowSteps.data?.length ?? 0) > 0 && (
            <SharedTableWrap>
              <SharedTable>
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {flowSteps.data?.map((step) => (
                    <tr key={step.id}>
                      <td>{step.step_order}</td>
                      <td>{step.step_type}</td>
                      <td>
                        <SharedDangerButton
                          type="button"
                          onClick={async () => {
                            try {
                              await deleteFlowStep.mutateAsync({
                                id: step.id,
                                flowId: step.flow_id,
                              });
                            } catch (error) {
                              handleError(error, 'Failed to delete flow step.');
                            }
                          }}
                        >
                          Delete
                        </SharedDangerButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </SharedTable>
            </SharedTableWrap>
          )}
        </SectionCard>

        <SectionCard>
          <SectionTitle>Email Campaigns</SectionTitle>
          <SectionHint>Create draft campaigns and connect templates/flows.</SectionHint>
          <FormRow
            onSubmit={async (event) => {
              event.preventDefault();
              if (!campaignName.trim()) return;
              try {
                await createCampaign.mutateAsync({
                  name: campaignName.trim(),
                  channel: campaignChannel,
                  templateId: campaignTemplateId || null,
                  flowId: campaignFlowId || null,
                });
                setCampaignName('');
                setCampaignTemplateId('');
                setCampaignFlowId('');
              } catch (error) {
                handleError(error, 'Failed to create campaign.');
              }
            }}
          >
            <Input
              placeholder="Campaign name"
              value={campaignName}
              onChange={(event) => setCampaignName(event.target.value)}
            />
            <Select
              value={campaignChannel}
              onChange={(event) => setCampaignChannel(event.target.value as 'sms' | 'email')}
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </Select>
            <Select
              value={campaignTemplateId}
              onChange={(event) => setCampaignTemplateId(event.target.value)}
            >
              <option value="">No template</option>
              {templateOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Select value={campaignFlowId} onChange={(event) => setCampaignFlowId(event.target.value)}>
              <option value="">No flow</option>
              {flowOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <SharedPrimaryButton type="submit" disabled={createCampaign.isPending}>
              {createCampaign.isPending ? 'Saving...' : 'Add campaign'}
            </SharedPrimaryButton>
          </FormRow>

          {(campaigns.data?.length ?? 0) === 0 && !campaigns.isLoading && !campaigns.error && (
            <Empty>No campaigns yet.</Empty>
          )}
          {campaigns.isLoading && <Loading>Loading campaigns...</Loading>}
          {campaigns.error && <ErrorText>Unable to load campaigns.</ErrorText>}
          {(campaigns.data?.length ?? 0) > 0 && (
            <SharedTableWrap>
              <SharedTable>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.data?.map((campaign) => (
                    <tr key={campaign.id}>
                      <td>{campaign.name}</td>
                      <td>{campaign.channel}</td>
                      <td>{campaign.status}</td>
                      <td>
                        <RowActions>
                          <SharedSecondaryButton
                            type="button"
                            onClick={async () => {
                              try {
                                const nextStatus = campaign.status === 'draft' ? 'scheduled' : 'draft';
                                await updateCampaign.mutateAsync({ id: campaign.id, changes: { status: nextStatus } });
                              } catch (error) {
                                handleError(error, 'Failed to update campaign status.');
                              }
                            }}
                          >
                            Toggle draft/scheduled
                          </SharedSecondaryButton>
                          <SharedDangerButton
                            type="button"
                            onClick={async () => {
                              if (!window.confirm('Delete this campaign?')) return;
                              try {
                                await deleteCampaign.mutateAsync(campaign.id);
                              } catch (error) {
                                handleError(error, 'Failed to delete campaign.');
                              }
                            }}
                          >
                            Delete
                          </SharedDangerButton>
                        </RowActions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </SharedTable>
            </SharedTableWrap>
          )}
          <ComingSoon>Sending and delivery tracking will be added in a later phase.</ComingSoon>
        </SectionCard>

        <SectionCard>
          <SectionTitle>Text-to-Join Keywords</SectionTitle>
          <SectionHint>
            Register keywords and wire them to templates or flows.{' '}
            <Link to="/marketing/keywords" style={{ color: 'var(--primary)', fontWeight: 600 }}>
              Keyword Management {'->'}
            </Link>
          </SectionHint>
          <FormRow
            onSubmit={async (event) => {
              event.preventDefault();
              if (!keyword.trim()) return;
              try {
                await createKeyword.mutateAsync({
                  keyword,
                  description: keywordDescription || null,
                  responseTemplateId: keywordTemplateId || null,
                  flowId: keywordFlowId || null,
                });
                setKeyword('');
                setKeywordDescription('');
                setKeywordTemplateId('');
                setKeywordFlowId('');
              } catch (error) {
                handleError(error, 'Failed to create keyword.');
              }
            }}
          >
            <Input
              placeholder="Keyword (e.g. JOIN)"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
            />
            <Input
              placeholder="Description"
              value={keywordDescription}
              onChange={(event) => setKeywordDescription(event.target.value)}
            />
            <Select value={keywordTemplateId} onChange={(event) => setKeywordTemplateId(event.target.value)}>
              <option value="">No response template</option>
              {templateOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Select value={keywordFlowId} onChange={(event) => setKeywordFlowId(event.target.value)}>
              <option value="">No flow</option>
              {flowOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <SharedPrimaryButton type="submit" disabled={createKeyword.isPending}>
              {createKeyword.isPending ? 'Saving...' : 'Add keyword'}
            </SharedPrimaryButton>
          </FormRow>

          {(keywords.data?.length ?? 0) === 0 && !keywords.isLoading && !keywords.error && (
            <Empty>No keywords yet.</Empty>
          )}
          {keywords.isLoading && <Loading>Loading keywords...</Loading>}
          {keywords.error && <ErrorText>Unable to load keywords.</ErrorText>}
          {(keywords.data?.length ?? 0) > 0 && (
            <SharedTableWrap>
              <SharedTable>
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keywords.data?.map((item) => (
                    <tr key={item.id}>
                      <td>{item.keyword}</td>
                      <td>{item.is_active ? 'Yes' : 'No'}</td>
                      <td>
                        <RowActions>
                          <SharedSecondaryButton
                            type="button"
                            onClick={async () => {
                              try {
                                await updateKeyword.mutateAsync({
                                  id: item.id,
                                  changes: { is_active: !item.is_active },
                                });
                              } catch (error) {
                                handleError(error, 'Failed to toggle keyword status.');
                              }
                            }}
                          >
                            Toggle
                          </SharedSecondaryButton>
                          <SharedDangerButton
                            type="button"
                            onClick={async () => {
                              if (!window.confirm('Delete this keyword?')) return;
                              try {
                                await deleteKeyword.mutateAsync(item.id);
                              } catch (error) {
                                handleError(error, 'Failed to delete keyword.');
                              }
                            }}
                          >
                            Delete
                          </SharedDangerButton>
                        </RowActions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </SharedTable>
            </SharedTableWrap>
          )}
        </SectionCard>

        <SectionCard>
          <SectionTitle>Tags</SectionTitle>
          <SectionHint>Create marketing tag sets for segmentation.</SectionHint>
          <FormRow
            onSubmit={async (event) => {
              event.preventDefault();
              if (!tagName.trim()) return;
              try {
                await createTagSet.mutateAsync({
                  name: tagName.trim(),
                  description: tagDescription || null,
                  color: tagColor || null,
                });
                setTagName('');
                setTagDescription('');
              } catch (error) {
                handleError(error, 'Failed to create tag set.');
              }
            }}
          >
            <Input
              placeholder="Tag set name"
              value={tagName}
              onChange={(event) => setTagName(event.target.value)}
            />
            <Input
              placeholder="Description"
              value={tagDescription}
              onChange={(event) => setTagDescription(event.target.value)}
            />
            <Input type="color" value={tagColor} onChange={(event) => setTagColor(event.target.value)} />
            <SharedPrimaryButton type="submit" disabled={createTagSet.isPending}>
              {createTagSet.isPending ? 'Saving...' : 'Add tag set'}
            </SharedPrimaryButton>
          </FormRow>

          {(tagSets.data?.length ?? 0) === 0 && !tagSets.isLoading && !tagSets.error && (
            <Empty>No tag sets yet.</Empty>
          )}
          {tagSets.isLoading && <Loading>Loading tag sets...</Loading>}
          {tagSets.error && <ErrorText>Unable to load tag sets.</ErrorText>}
          {(tagSets.data?.length ?? 0) > 0 && (
            <SharedTableWrap>
              <SharedTable>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Color</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tagSets.data?.map((tagSet) => (
                    <tr key={tagSet.id}>
                      <td>{tagSet.name}</td>
                      <td>{tagSet.color ?? '-'}</td>
                      <td>
                        <RowActions>
                          <SharedSecondaryButton
                            type="button"
                            onClick={async () => {
                              const nextName = window.prompt('Rename tag set', tagSet.name)?.trim();
                              if (!nextName || nextName === tagSet.name) return;
                              try {
                                await updateTagSet.mutateAsync({
                                  id: tagSet.id,
                                  changes: { name: nextName },
                                });
                              } catch (error) {
                                handleError(error, 'Failed to rename tag set.');
                              }
                            }}
                          >
                            Rename
                          </SharedSecondaryButton>
                          <SharedDangerButton
                            type="button"
                            onClick={async () => {
                              if (!window.confirm('Delete this tag set?')) return;
                              try {
                                await deleteTagSet.mutateAsync(tagSet.id);
                              } catch (error) {
                                handleError(error, 'Failed to delete tag set.');
                              }
                            }}
                          >
                            Delete
                          </SharedDangerButton>
                        </RowActions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </SharedTable>
            </SharedTableWrap>
          )}
        </SectionCard>
      </Grid>
    </Page>
  );
};

export default MarketingPage;
